import { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Form,
  Alert,
} from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExerciseCanvas from "./ExerciseCanvas";
import Preloader from "@/components/Elements/Preloader";
import VideoPlayer from "@/components/VideoPlayer";

import { markupApi } from "@/api";

import withRouter from "@/components/Elements/WithRouter";
import "../../scss/components/_field.scss";

const Exercises = (props) => {
  const [exercises, setExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [videoSource, setVideoSource] = useState(null);
  const [field, setField] = useState({
    width: 0,
    height: 0,
    points: [],
    zone: [],
    updated: null,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [formData, setFormData] = useState({
    kind: "exercise",
    time_start: "00:00:00",
    time_end: "00:00:00",
    zone: [],
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState("00:00:00");
  const [fps, setFps] = useState(25); // Default FPS for VideoPlayer
  const [isEditing, setIsEditing] = useState(false);
  // sync video player zoom/pan + sizing metrics for overlay canvas alignment
  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [videoMetrics, setVideoMetrics] = useState({
    videoDomWidth: 0,
    videoDomHeight: 0,
    videoWidth: 0,
    videoHeight: 0,
    ratio: 1,
    imageY: 0,
  });

  const canvasRef = useRef();
  const videoRef = useRef();

  const { videoId } = props.match.params;

  const getExercisesList = async (videoId) => {
    try {
      const response = await markupApi.getExercisesList(videoId);
      console.log("response", response);
      // Handle paginated response structure
      return response?.results || [];
    } catch (error) {
      console.error("Error fetching exercises:", error);
      return [];
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      const fieldMeta = await markupApi.getFieldMeta(videoId);

      const exercisesList = await getExercisesList(videoId);

      console.log("exercises list", exercisesList);

      // Sort exercises by ID in ascending order
      const sortedExercises = exercisesList.sort((a, b) => a.id - b.id);

      const { field: fieldData, source } = fieldMeta;

      setField(fieldData);
      setVideoSource(source);
      setExercises(sortedExercises);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      setIsLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const secondsToTimeString = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const handleFrameChange = useCallback((currentFrame) => {
    const currentTime = (currentFrame / fps);
    setCurrentVideoTime(secondsToTimeString(currentTime));
  }, [fps, secondsToTimeString]);

  // Handlers to sync zoom/pan and sizes from VideoPlayer
  const handleStageScaleChange = useCallback((scale) => {
    setStageScale(scale || 1);
  }, []);

  const handleStagePosChange = useCallback((x, y) => {
    setStagePos({ x: x || 0, y: y || 0 });
  }, []);

  const handleRatioChange = useCallback((r) => {
    setVideoMetrics((prev) => ({ ...prev, ratio: r || 1 }));
  }, []);

  const handleVideoDomSizeChange = useCallback((domW, domH) => {
    setVideoMetrics((prev) => {
      const videoDomWidth = domW || 0;
      const videoDomHeight = domH || 0;
      const { videoWidth, videoHeight } = prev;
      const imageY = videoWidth
        ? (videoDomHeight - (videoHeight * videoDomWidth) / videoWidth) / 2
        : 0;
      return {
        ...prev,
        videoDomWidth,
        videoDomHeight,
        imageY,
      };
    });
  }, []);

  const handleVideoSizeChange = useCallback((vW, vH) => {
    setVideoMetrics((prev) => {
      const videoWidth = vW || 0;
      const videoHeight = vH || 0;
      const { videoDomWidth, videoDomHeight } = prev;
      const imageY = videoWidth
        ? (videoDomHeight - (videoHeight * videoDomWidth) / videoWidth) / 2
        : 0;
      return {
        ...prev,
        videoWidth,
        videoHeight,
        imageY,
      };
    });
  }, []);

  const setTimeFromCurrent = useCallback(
    (field) => {
      const currentTime = videoRef.current?.currentTime || 0;
      const currentTimeString = secondsToTimeString(currentTime);

      setFormData((prev) => ({
        ...prev,
        [field]: currentTimeString,
      }));
    },
    [secondsToTimeString]
  );

  const handleAddExercise = useCallback(() => {
    const currentTime = videoRef.current?.currentTime || 0;
    const currentTimeString = secondsToTimeString(currentTime);
    const endTimeString = secondsToTimeString(currentTime + 60); // +1 minute

    setShowAddModal(true);
    setIsEditing(true);
    setCurrentExercise(null);
    setFormData({
      kind: "exercise",
      time_start: currentTimeString,
      time_end: endTimeString,
      zone: [],
    });
    setErrors({});
  }, [secondsToTimeString]);

  const handleEditExercise = useCallback((exercise) => {
    setShowEditModal(true);
    setIsEditing(true);
    setCurrentExercise(exercise);
    setFormData({
      kind: exercise.kind,
      time_start: exercise.time_start,
      time_end: exercise.time_end,
      zone: exercise.zone || [],
    });
    setErrors({});
    
    // Force update the canvas with existing exercise points
    console.log("Editing exercise with zone:", exercise.zone);
  }, []);

  const handleDeleteExercise = useCallback(
    async (exerciseId) => {
      if (!window.confirm("Are you sure you want to delete this exercise?")) {
        return;
      }

      try {
        await markupApi.deleteExercise(videoId, exerciseId);
        loadData();
      } catch (error) {
        console.error("Error deleting exercise:", error);
        alert("Error deleting exercise");
      }
    },
    [videoId, loadData]
  );

  const handleTimeChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleKindChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      kind: value,
    }));
  }, []);

  const handleZoneUpdate = useCallback((zone) => {
    console.log("zone", zone);
    setFormData((prev) => ({
      ...prev,
      zone: zone,
    }));
  }, []);

  const jumpToTime = useCallback((timeString) => {
    if (!timeString || !videoRef.current) return;

    const timeParts = timeString.split(":");
    const seconds =
      parseInt(timeParts[0]) * 3600 +
      parseInt(timeParts[1]) * 60 +
      parseInt(timeParts[2]);

    videoRef.current.currentTime = seconds;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.time_start) {
      newErrors.time_start = "Start time is required";
    }

    if (!formData.time_end) {
      newErrors.time_end = "End time is required";
    }

    if (formData.time_start && formData.time_end) {
      const startTime = new Date(`2000-01-01T${formData.time_start}`);
      const endTime = new Date(`2000-01-01T${formData.time_end}`);

      if (startTime >= endTime) {
        newErrors.time_end = "End time must be after start time";
      }
    }

    if (!formData.zone || formData.zone.length < 2) {
      newErrors.zone =
        "Exactly 2 zone points are required (top-left and bottom-right)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSaveExercise = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (currentExercise) {
        // Update existing exercise
        await markupApi.updateExercise(videoId, currentExercise.id, formData);
      } else {
        // Create new exercise
        await markupApi.createExercise(videoId, formData);
        
        // After creating a new exercise, jump to the end time for editing
        if (formData.time_end) {
          jumpToTime(formData.time_end);
        }
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setIsEditing(false);
      setIsSaving(false);

      loadData();
    } catch (error) {
      console.error("Error saving exercise:", error);
      alert("Error saving exercise");
      setIsSaving(false);
    }
  }, [validateForm, currentExercise, videoId, formData, loadData, jumpToTime]);

  const formatTime = useCallback((timeString) => {
    if (!timeString) return "00:00:00";
    return timeString;
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowAddModal(false);
    setShowEditModal(false);
    setIsEditing(false);
  }, []);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <>
      {videoSource ? (
        <Container className="p-3" fluid>
          <Row>
            <Col>
              <div className="viewer-wrapper">
                <div className="viewer position-relative">
                  <VideoPlayer
                    ref={videoRef}
                    src={videoSource}
                    fps={fps}
                    videoProps={{
                      autoPlay: false,
                      loop: false,
                    }}
                    frameStep={1}
                    autoZoom={false}
                    shouldFocusOnBall={false}
                    tracks={{}}
                    colors={{}}
                    zoomButtonsVisible={!isEditing}
                    wheelZoomNeedsCtrl={true}
                    onFrameChange={handleFrameChange}
                    onStageScaleChange={handleStageScaleChange}
                    onStagePosChange={handleStagePosChange}
                    onRatioChange={handleRatioChange}
                    onVideoDomSizeChange={handleVideoDomSizeChange}
                    onVideoSizeChange={handleVideoSizeChange}
                    onMouseDownHandler={isEditing ? () => {} : undefined}
                    onMouseMoveHandler={isEditing ? () => {} : undefined}
                    onMouseUpHandler={isEditing ? () => {} : undefined}
                  />
                  <ExerciseCanvas
                    ref={canvasRef}
                    zone={formData.zone}
                    exercises={exercises}
                    isEditing={isEditing}
                    transform={{
                      stageScale,
                      stageX: stagePos.x,
                      stageY: stagePos.y,
                    }}
                    metrics={videoMetrics}
                    onZoneChange={handleZoneUpdate}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {/* Exercise Form Section - Below Video */}
          {(showAddModal || showEditModal) && (
            <Row className="mt-3">
              <Col>
                <div className="border rounded p-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      {showAddModal ? "Add New Exercise" : "Edit Exercise"}
                    </h5>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCloseModal}
                    >
                      <FontAwesomeIcon icon={["fas", "times"]} />
                    </Button>
                  </div>

                  <Form>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Exercise Type</Form.Label>
                          <Form.Select
                            value={formData.kind}
                            onChange={(e) => handleKindChange(e.target.value)}
                          >
                            <option value="exercise">Exercise</option>
                            <option value="internal_match">Internal Match</option>
                            <option value="rondo">Rondo</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Time</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              type="time"
                              step="1"
                              value={formData.time_start}
                              onChange={(e) =>
                                handleTimeChange("time_start", e.target.value)
                              }
                              isInvalid={!!errors.time_start}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setTimeFromCurrent("time_start")}
                              title="Set to current video time"
                            >
                              Now
                            </Button>
                          </div>
                          <Form.Control.Feedback type="invalid">
                            {errors.time_start}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Time</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              type="time"
                              step="1"
                              value={formData.time_end}
                              onChange={(e) =>
                                handleTimeChange("time_end", e.target.value)
                              }
                              isInvalid={!!errors.time_end}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setTimeFromCurrent("time_end")}
                              title="Set to current video time"
                            >
                              Now
                            </Button>
                          </div>
                          <Form.Control.Feedback type="invalid">
                            {errors.time_end}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <div className="text-muted small mb-3">
                          Current video time: {currentVideoTime}
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="secondary"
                            onClick={handleCloseModal}
                            size="sm"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleSaveExercise}
                            disabled={isSaving}
                            size="sm"
                          >
                            {isSaving ? (
                              <>
                                <FontAwesomeIcon
                                  spin
                                  icon={["fas", "spinner"]}
                                />{" "}
                                Saving...
                              </>
                            ) : (
                              "Save Exercise"
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>Zone Coordinates</Form.Label>
                          <div
                            className="border rounded p-3"
                            style={{ minHeight: "100px" }}
                          >
                            {(showAddModal || showEditModal) && (
                              <div
                                className="alert alert-info mb-3"
                                role="alert"
                              >
                                <strong>How to create an exercise zone:</strong>
                                <ul className="mb-0 mt-2">
                                  <li>
                                    Click on field points above to select them
                                    (they turn red when selected)
                                  </li>
                                  <li>
                                    Drag points to adjust their positions if
                                    needed
                                  </li>
                                  <li>
                                    Select exactly 2 points to create a
                                    rectangular zone (top-left and bottom-right)
                                  </li>
                                  <li>Click "Clear Zone" to start over</li>
                                </ul>
                              </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <p className="text-muted mb-0 small">
                                <strong>
                                  Click on field points above to select zone
                                  coordinates.
                                </strong>{" "}
                                Exactly 2 points required (top-left and
                                bottom-right).
                                {showAddModal || showEditModal ? (
                                  <span className="text-success ms-2">
                                    ✓ Canvas is active for editing
                                  </span>
                                ) : (
                                  <span className="text-muted ms-2">
                                    Canvas is inactive
                                  </span>
                                )}
                              </p>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  disabled
                                  title="Selection mode active"
                                >
                                  <FontAwesomeIcon
                                    icon={["fas", "mouse-pointer"]}
                                  />{" "}
                                  Select Points
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleZoneUpdate([])}
                                >
                                  Clear Zone
                                </Button>
                              </div>
                            </div>
                            {formData.zone.length > 0 && (
                              <div className="mb-2">
                                <strong>Selected points:</strong>{" "}
                                {formData.zone.length}
                                <div className="mt-2">
                                  {formData.zone.map((point, index) => (
                                    <div
                                      key={index}
                                      className="text-muted small d-inline-block me-3"
                                    >
                                      {point.desc || `Point ${point.id}`}: (
                                      {point.x.toFixed(3)}, {point.y.toFixed(3)}
                                      )
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {errors.zone && (
                              <Alert variant="danger" className="mt-2">
                                {errors.zone}
                              </Alert>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </Col>
            </Row>
          )}

          {/* Exercises Table */}
          <Row className="mt-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Training Exercises</h4>
                <Button variant="primary" onClick={handleAddExercise}>
                  <FontAwesomeIcon icon={["fas", "plus"]} /> Add Exercise
                </Button>
              </div>

              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Zone Points</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exercises.map((exercise) => (
                    <tr key={exercise.id}>
                      <td>{exercise.id}</td>
                      <td>{exercise.kind}</td>
                      <td>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => jumpToTime(exercise.time_start)}
                        >
                          {formatTime(exercise.time_start)}
                        </Button>
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => jumpToTime(exercise.time_end)}
                        >
                          {formatTime(exercise.time_end)}
                        </Button>
                      </td>
                      <td>
                        {exercise.zone ? "2 points (rectangle)" : "0 points"}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditExercise(exercise)}
                        >
                          <FontAwesomeIcon icon={["fas", "edit"]} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteExercise(exercise.id)}
                        >
                          <FontAwesomeIcon icon={["fas", "trash"]} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {exercises.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No exercises found. Click "Add Exercise" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      ) : (
        <Preloader />
      )}
    </>
  );
};

export default withRouter(Exercises);
